import moment from 'moment'
import socketClient from 'socket.io-client'
import api from '@/apis'
import globalConfig from '@/../config.json'

// Helper function to get TTS config based on language
// Reads tts_language_map from config.json for easy deployment customization
const getTTSConfig = (socketLanguage) => {
  const ttsLanguageMap = globalConfig.tts_language_map || {}
  const config = ttsLanguageMap[socketLanguage]
  if (config) {
    return config
  }
  // Fallback to environment variables or defaults
  return {
    speaker: process.env.VUE_APP_TTS_SPEAKER || 'zh-TW-HsiaoChenNeural',
    language: process.env.VUE_APP_TTS_LANGUAGE || 'mandarin'
  }
}

export default {
  state: {
    ip_addr: null,
    session_id: null,
    socket_server: null,
    additional_query_params: {}
  },
  mutations: {
    INIT_SOCKET_SERVER(state, { socket_server, language }) {
      state.socket_server = socket_server

      // Auto register when connected to server
      state.socket_server.on('connect', () => {
        state.socket_server.emit('register', { ip_addr: state.ip_addr, language })
      })

      // Add event listener
      state.socket_server.on('register_result', (data) => {
        this.dispatch('socketRegisterResultListener', data)
      })
      state.socket_server.on('nlp_result', (data) => {
        this.dispatch('socketNLPResultListener', data)
      })
      state.socket_server.on('notification_result', (data) => {
        this.dispatch('socketNotificationResultListener', data)
      })
    },
    DISCONNECT_SOCKET_SERVER(state) {
      if (state.socket_server) state.socket_server.disconnect()
    },
    SET_SOCKET_SESSION_ID(state, payload) {
      state.session_id = payload
    },
    SET_SOCKET_IP_ADDRESS(state, payload) {
      state.ip_addr = payload
    },
    ADD_SOCKET_ADDITIONAL_QUERY_PARAMS(state, payload) {
      state.additional_query_params = { ...state.additional_query_params, ...payload }
    }
  },
  actions: {
    updateSocketServer({ rootState, commit }) {
      try {
        // Disconnect from the previous server before connecting to new server
        commit('DISCONNECT_SOCKET_SERVER')

        // Connect To Socket Server
        const socket_server = socketClient(process.env.VUE_APP_SOCKET_SERVER)
        commit('INIT_SOCKET_SERVER', { socket_server, language: rootState.i18n.socket_language })
      } catch (error) {
        console.log('updateSocketServer', error.message)
      }
    },
    // Send message to Voicebot Socket
    async sendMessageToSocket({ state, commit }, { title, value }) {
      try {
        const ignore_words = ['_']
        const datetime = moment().format('hh:mm A')
        const ignore_word_check = ignore_words.filter((word) => title.includes(word))

        let display_text = title
        if (process.env.VUE_APP_ENABLE_MASKING) {
          const response = await api.newMaskedText({ text: title })
          if (response.data.success) display_text = response.data.data.masked_text
        }

        if (!ignore_word_check.length) commit('APPEND_CHAT_HISTORY', { type: 'user', datetime, messages: [{ type: 'text', text: display_text }] })
        commit('APPEND_CHAT_HISTORY', { type: 'ai', loading: true, datetime: moment().format('hh:mm A'), messages: [{}] })
        state.socket_server.emit('user_text', value)
      } catch (error) {
        console.log(error)
      }
    },
    sendAudioToSocket({ state }, audio) {
      state.socket_server.emit('user_speak', audio)
    },
    async uploadFileToServer({ commit, state }, file) {
      try {
        const datetime = moment().format('hh:mm A')

        // Show user message with file info
        const fileMessage = {
          type: 'user',
          datetime,
          messages: [
            {
              type: 'text',
              text: `📎 ${file.name} (${(file.size / 1024).toFixed(2)} KB)`
            }
          ]
        }
        commit('APPEND_CHAT_HISTORY', fileMessage)

        // Show loading bubble
        commit('APPEND_CHAT_HISTORY', { type: 'ai', loading: true, datetime: moment().format('hh:mm A'), messages: [{}] })

        // Check if upload endpoint is configured (not the dummy URL)
        const uploadEndpoint = process.env.VUE_APP_FILE_UPLOAD_ENDPOINT
        const isDummyEndpoint = !uploadEndpoint || uploadEndpoint.includes('your-upload-endpoint')

        let response
        if (isDummyEndpoint) {
          // Use dummy response for testing when endpoint is not ready
          console.log('🧪 Using dummy file upload response for testing')
          await new Promise((resolve) => setTimeout(resolve, 1500)) // Simulate upload delay

          response = {
            data: {
              success: true,
              message: `✓ File "${file.name}" uploaded successfully`,
              file_url: `https://dummy-storage.example.com/files/${Date.now()}_${file.name}`,
              file_info: {
                filename: file.name,
                size: file.size,
                type: file.type,
                uploaded_at: new Date().toISOString()
              }
            }
          }
        } else {
          // Real upload to server
          const formData = new FormData()
          formData.append('file', file)
          formData.append('session_id', state.session_id)
          response = await api.uploadFile(formData)
        }

        // Handle server response
        if (response.data.success) {
          // Send file info through socket if needed
          const fileInfo = {
            filename: file.name,
            size: file.size,
            type: file.type,
            url: response.data.file_url || response.data.url,
            is_demo: isDummyEndpoint
          }
          state.socket_server.emit('user_file', fileInfo)

          // The loading bubble will be replaced by APPEND_CHAT_HISTORY
          // because it looks for loading bubble and replaces it
          commit('APPEND_CHAT_HISTORY', {
            type: 'ai',
            loading: false,
            datetime: moment().format('hh:mm A'),
            messages: [{ type: 'text', text: response.data.message || '✓ File uploaded successfully' }]
          })
        } else {
          throw new Error(response.data.message || 'Upload failed')
        }
      } catch (error) {
        console.error('uploadFileToServer error:', error)
        throw error
      }
    },
    socketRegisterResultListener({ rootState, state, commit, dispatch }, payload) {
      const socket_language = rootState.i18n.socket_language
      const { success, message, data } = payload
      if (success) {
        commit('SET_SOCKET_SESSION_ID', data.session_id)
        if (data.general) commit('SET_INTERFACE_GENERAL', data.general)
        if (data.top_section) commit('SET_INTERFACE_TOP_SECTION', data.top_section)
        if (data.bottom_section) commit('SET_INTERFACE_BOTTOM_SECTION', data.bottom_section)
        commit('SET_CHAT_LOADING', false)

        const additional_query_params = state.additional_query_params
        const init_message = Object.keys(additional_query_params).length > 0 ? `init_${socket_language}_${JSON.stringify(additional_query_params)}` : `init_${socket_language}`
        dispatch('sendMessageToSocket', { title: init_message, value: init_message })
      } else console.log(message)
    },
    async socketNLPResultListener({ commit, rootState }, payload) {
      const { messages, is_bottom_menu_show, bottom_menu, input_disabled, input_placeholder } = payload
      commit('RESET_CHAT_INPUT_BAR')
      if (bottom_menu) commit('INIT_CHAT_BOTTOM_MENU', { is_bottom_menu_show, bottom_menu })
      if (input_disabled) commit('SET_CHAT_INPUT_BAR', { input_disabled, input_placeholder })

      // Process messages and add TTS audio if enabled
      if (messages.length) {
        const processedMessages = [...messages]

        // Check if TTS is enabled
        const isTTSEnabled = process.env.VUE_APP_ENABLE_TTS === 'true'

        if (isTTSEnabled) {
          // Process each message for TTS
          for (let i = 0; i < processedMessages.length; i++) {
            const message = processedMessages[i]

            // Handle streaming_text completion - generate TTS for accumulated text
            if (message.type === 'streaming_text' && message.is_complete === true) {
              try {
                // Get accumulated text from chat_history
                const chatHistory = rootState.chat.chat_history
                const lastStreamingBubble = [...chatHistory].reverse().find((chat) => chat.messages?.[0]?.type === 'streaming_text')

                if (lastStreamingBubble && lastStreamingBubble.messages[0]) {
                  const fullText = lastStreamingBubble.messages[0].text

                  if (fullText && fullText.trim() !== '') {
                    console.log(`🎤 Streaming complete, generating TTS for: "${fullText.substring(0, 50)}..."`)

                    const ttsEndpoint = process.env.VUE_APP_TTS_API_ENDPOINT
                    const isDummyEndpoint = !ttsEndpoint || ttsEndpoint.includes('your-tts-endpoint')

                    let ttsAudioUrl
                    let audioLength = null

                    if (isDummyEndpoint) {
                      console.log('🧪 TTS Demo Mode: Using dummy audio URL for streaming')
                      ttsAudioUrl = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3'
                    } else {
                      // Get TTS config based on current language
                      const socketLanguage = rootState.i18n.socket_language
                      const ttsConfig = getTTSConfig(socketLanguage)
                      console.log(`🌐 TTS Language: ${socketLanguage} → speaker: ${ttsConfig.speaker}`)

                      const ttsResponse = await api.textToSpeech({
                        text: fullText,
                        file_type: 'mp3',
                        compression_level: 4,
                        sampling_rate: 22050,
                        speed: 1,
                        voice: process.env.VUE_APP_TTS_VOICE || 'microsoft',
                        speaker: ttsConfig.speaker,
                        language: ttsConfig.language,
                        is_cache: true
                      })

                      if (ttsResponse.data.success && ttsResponse.data.data.audio) {
                        ttsAudioUrl = ttsResponse.data.data.audio
                        audioLength = ttsResponse.data.data.audio_length
                        const isFromCache = ttsResponse.data.data.is_from_cache
                        console.log(`✅ TTS Audio generated for streaming: ${audioLength}s ${isFromCache ? '(from cache)' : ''}`)
                      }
                    }

                    // Add TTS audio message after streaming text completes
                    if (ttsAudioUrl) {
                      processedMessages.splice(i + 1, 0, {
                        type: 'audio',
                        audio_url: ttsAudioUrl,
                        audio_length: audioLength
                      })
                      i++
                    }
                  }
                }
              } catch (error) {
                console.error('TTS Error for streaming:', error)
              }
            }
            // Handle regular text messages
            else if (message.type === 'text' && message.text && message.text.trim() !== '') {
              try {
                // Check if TTS endpoint is configured (not the dummy URL)
                const ttsEndpoint = process.env.VUE_APP_TTS_API_ENDPOINT
                const isDummyEndpoint = !ttsEndpoint || ttsEndpoint.includes('your-tts-endpoint')

                let ttsAudioUrl
                let audioLength = null

                if (isDummyEndpoint) {
                  // Demo mode: use dummy audio URL
                  console.log('🧪 TTS Demo Mode: Using dummy audio URL')
                  ttsAudioUrl = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3'
                } else {
                  // Get TTS config based on current language
                  const socketLanguage = rootState.i18n.socket_language
                  const ttsConfig = getTTSConfig(socketLanguage)
                  console.log(`🌐 TTS Language: ${socketLanguage} → speaker: ${ttsConfig.speaker}`)

                  // Real TTS API call
                  const ttsResponse = await api.textToSpeech({
                    text: message.text,
                    file_type: 'mp3',
                    compression_level: 4,
                    sampling_rate: 22050,
                    speed: 1,
                    voice: process.env.VUE_APP_TTS_VOICE || 'microsoft',
                    speaker: ttsConfig.speaker,
                    language: ttsConfig.language,
                    is_cache: true
                  })

                  if (ttsResponse.data.success && ttsResponse.data.data.audio) {
                    ttsAudioUrl = ttsResponse.data.data.audio
                    audioLength = ttsResponse.data.data.audio_length
                    const isFromCache = ttsResponse.data.data.is_from_cache

                    console.log(`✅ TTS Audio generated: ${audioLength}s ${isFromCache ? '(from cache)' : ''}`)
                  }
                }

                // Add TTS audio message after text message if we have audio URL
                if (ttsAudioUrl) {
                  processedMessages.splice(i + 1, 0, {
                    type: 'audio',
                    audio_url: ttsAudioUrl,
                    audio_length: audioLength
                  })
                  i++ // Skip the newly added audio message
                }
              } catch (error) {
                console.error('TTS Error:', error)
                // Continue processing other messages even if TTS fails
              }
            }
          }
        }

        commit('APPEND_CHAT_HISTORY', {
          type: 'ai',
          loading: false,
          datetime: moment().format('hh:mm A'),
          messages: processedMessages
        })
      }
    },
    socketNotificationResultListener({ dispatch }, { data }) {
      dispatch('socketNLPResultListener', data)
    }
  }
}
